import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

// Type definitions for the eNO1 status data
export interface QuarterData {
  ppo: string;
  date: string;
  version: number | string;
  status: string;
}

export interface ENO1StatusItem {
  ppo: string;
  year: string | number;
  version?: string | number;
  status?: string;
  closed?: boolean;
  quarters: {
    q1?: QuarterData;
    q2?: QuarterData;
    q3?: QuarterData;
    q4?: QuarterData;
  };
}

export interface ENO1StatusResponse {
  inbound: ENO1StatusItem[];
  outbound: ENO1StatusItem[];
}

// Parameters for filtering the status data
export interface ENO1StatusParams {
  year?: string;
  ppo?: string;
  inbound?: boolean;
  open?: boolean;
  timestamp?: number;
}

// Query key for consistent reference
export const eno1StatusQueryKey = ['eno1-status'];

/**
 * Fetches eNO1 status data from the API
 * @param params - Optional parameters for filtering data by year and PPO
 * @returns Promise with the eNO1 status data
 */
const fetchENO1Status = async (params?: ENO1StatusParams): Promise<ENO1StatusResponse> => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params?.year) {
      queryParams.append('year', params.year);
    }
    
    if (params?.ppo) {
      queryParams.append('ppo', params.ppo);
    }
    
    if (params?.inbound !== undefined) {
      queryParams.append('inbound', params.inbound.toString());
    }
    
    if (params?.open !== undefined) {
      queryParams.append('open', params.open.toString());
    }
    
    const queryString = queryParams.toString();
    const url = `/data-set/eno1/status${queryString ? `?${queryString}` : ''}`;
    
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching eNO1 status data:', error);
    throw error;
  }
};

/**
 * Hook for fetching and managing eNO1 status data
 * @param params - Optional parameters for filtering data by year and PPO
 * @returns Query result with eNO1 status data, loading state, error state, and refetch function
 */
export function useENO1Status(params?: ENO1StatusParams) {
  return useQuery<ENO1StatusResponse>({
    queryKey: [...eno1StatusQueryKey, params],
    queryFn: () => fetchENO1Status(params),
    retry: 2,
    retryDelay: 1000,
    staleTime: 60000, // 1 minute
  });
} 